import { useCallback, useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Select,
  Spinner,
  Text,
  UnorderedList,
  useToast,
  VStack,
} from "@chakra-ui/react";
import ReactJson from "react-json-view";
import { useAuthenticatedRequest } from "../../hooks/useAuthenticatedRequest";
import { unwrapApiResponse } from "../../utils/unwrapApiResponse";
import { showApiErrorToast } from "../../utils/showApiToast";

const DOCUMENTS_BASE = "https://documents.poc.mrc.ai/api";
const CODIFY_BASE = "https://codify.poc.mrc.ai/api";
const POLL_MS = 2000;

export const STATE_COLOURS = { valid: "green", invalid: "orange", codified: "orange", failed: "red", queued: "blue", running: "blue" };
const ROW_COLOURS = { codified: "green", error: "red", new: "gray" };
export const isActive = (run) => ["queued", "running"].includes(run?.state);

const asList = (data) => (Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : []);
const raw = (v) => v?.$date ?? v;
const when = (v) => (raw(v) ? new Date(raw(v)).toLocaleString() : "");
const time = (v) => (raw(v) ? new Date(raw(v)).getTime() : 0);
const parse = (text) => {
  if (text == null || text === "") return null;
  if (typeof text === "object") return text;
  try {
    return JSON.parse(text);
  } catch {
    return undefined; // not JSON: shown as text
  }
};

const StateBadge = ({ state }) => (
  <Badge colorScheme={STATE_COLOURS[state] || "gray"} textTransform="none">{state || "—"}</Badge>
);

const Json = ({ value }) => {
  const parsed = parse(value);
  if (parsed === null) return <Text fontSize="xs" color="gray.500">Nothing</Text>;
  if (parsed === undefined || typeof parsed !== "object") return <Text fontSize="xs" whiteSpace="pre-wrap">{String(value)}</Text>;
  return (
    <ReactJson src={parsed} name={false} collapsed={2} displayDataTypes={false} displayObjectSize={false}
      enableClipboard indentWidth={2} style={{ fontSize: "11px", backgroundColor: "white" }} />
  );
};

/** One codification run: progress, problems, each row and the assembled output. */
const RunDetail = ({ run, onRowClick, onCopy }) => {
  const total = run.rowsTotal || run.rows?.length || 0;
  const done = run.rowsDone || 0;
  return (
    <VStack align="stretch" spacing={4}>
      <Box>
        <HStack justify="space-between" mb={1}>
          <HStack>
            <StateBadge state={run.state} />
            <Text fontSize="sm" fontWeight="semibold">{run.specificationName || "Codification"}</Text>
          </HStack>
          {isActive(run) && <Spinner size="sm" color="blue.500" />}
        </HStack>
        <Progress value={total ? (done / total) * 100 : 0} size="sm" borderRadius="full" colorScheme={STATE_COLOURS[run.state] || "blue"}
          isIndeterminate={run.state === "queued" && !total} />
        <HStack justify="space-between" fontSize="xs" color="gray.600" mt={1}>
          <Text>
            {done} / {total} rows{run.rowsFailed ? ` · ${run.rowsFailed} failed` : ""}
          </Text>
          <Text>
            {run.contractSchemaId ? `${run.contractSchemaId} v${run.contractSchemaVersion}` : ""}
            {run.trigger ? ` · ${run.trigger}` : ""}
          </Text>
        </HStack>
        <Text fontSize="xs" color="gray.500">
          {run.startedDate && `Started ${when(run.startedDate)}`}
          {run.completedDate && ` · completed ${when(run.completedDate)}`}
        </Text>
      </Box>

      {run.state === "failed" && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <AlertDescription fontSize="sm">{run.error || "The codification failed."}</AlertDescription>
        </Alert>
      )}
      {run.state === "invalid" && (
        <Alert status="warning" borderRadius="md" alignItems="start">
          <AlertIcon />
          <Box>
            <AlertTitle fontSize="sm">The output doesn't match the schema</AlertTitle>
            <UnorderedList fontSize="xs" mt={1}>
              {(run.validationErrors || []).map((e, i) => <ListItem key={i}>{e}</ListItem>)}
            </UnorderedList>
          </Box>
        </Alert>
      )}

      <Box>
        <Heading size="xs" mb={2}>Rows</Heading>
        {(run.rows || []).length === 0 ? (
          <Text fontSize="sm" color="gray.500">{isActive(run) ? "Waiting for the rows…" : "No rows."}</Text>
        ) : (
          <Accordion allowMultiple borderWidth="1px" borderRadius="md" bg="white">
            {run.rows.map((row, i) => (
              <AccordionItem key={row.id || i}>
                <AccordionButton py={2}>
                  <HStack flex="1" spacing={2} textAlign="left" minW={0}>
                    <Badge colorScheme={ROW_COLOURS[row.state] || "gray"} textTransform="none" flexShrink={0}>{row.state || "new"}</Badge>
                    <Text fontSize="sm" fontWeight="medium" noOfLines={1}>{row.heading || row.type || "Row"}</Text>
                    <Text fontSize="xs" color="gray.500" noOfLines={1}>{row.type}{row.segment ? ` · ${row.segment}` : ""}</Text>
                  </HStack>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={3}>
                  {row.id && onRowClick && (
                    <Button size="xs" variant="link" colorScheme="blue" mb={2} onClick={() => onRowClick(row.id)}>Show in the document</Button>
                  )}
                  {row.state === "error" ? (
                    <Text fontSize="xs" color="red.600" whiteSpace="pre-wrap">{row.error || "This row failed."}</Text>
                  ) : (
                    <Json value={row.data} />
                  )}
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </Box>

      <Box>
        <HStack justify="space-between" mb={2}>
          <Heading size="xs">Contract JSON</Heading>
          <Button size="xs" onClick={() => onCopy(run.output)} isDisabled={!run.output}>Copy JSON</Button>
        </HStack>
        <Box borderWidth="1px" borderRadius="md" bg="white" p={2} maxH="420px" overflowY="auto">
          <Json value={run.output} />
        </Box>
      </Box>
    </VStack>
  );
};

/**
 * Codify a whole document with a specification, as a server-side run
 * (POST /documents/{id}/codify), and show that run and the document's past
 * runs. isStartOpen opens the Start modal (from the toolbar).
 */
const DocumentCodifyRuns = ({ documentId, defaultSpecificationId, isStartOpen, onStartClose, onRowClick }) => {
  const toast = useToast();
  const { get, post } = useAuthenticatedRequest();
  const request = useRef({ get, post });
  request.current = { get, post };

  const [specifications, setSpecifications] = useState(null);
  const [specificationId, setSpecificationId] = useState("");
  const [starting, setStarting] = useState(false);

  const [panelOpen, setPanelOpen] = useState(false);
  const [run, setRun] = useState(null);
  const [runs, setRuns] = useState(null);
  const pollTimer = useRef(null);

  const stopPolling = useCallback(() => {
    clearTimeout(pollTimer.current);
    pollTimer.current = null;
  }, []);
  useEffect(() => stopPolling, [stopPolling]);

  const loadRuns = useCallback(async () => {
    if (!documentId) return;
    try {
      const res = await request.current.get(`${DOCUMENTS_BASE}/codifications?documentId=${encodeURIComponent(documentId)}`);
      setRuns(asList(unwrapApiResponse(res.data)).sort((a, b) => time(b.completedDate || b.startedDate || b.createdDate) - time(a.completedDate || a.startedDate || a.createdDate)));
    } catch (err) {
      setRuns([]);
      showApiErrorToast(toast, err, { title: "Couldn't load the codification runs" });
    }
  }, [documentId, toast]);

  // Poll a run every 2 seconds until it's no longer queued or running
  const follow = useCallback(
    (runId) => {
      stopPolling();
      const tick = async () => {
        try {
          const res = await request.current.get(`${DOCUMENTS_BASE}/codifications/${encodeURIComponent(runId)}`);
          const latest = unwrapApiResponse(res.data);
          setRun(latest);
          if (isActive(latest)) pollTimer.current = setTimeout(tick, POLL_MS);
          else {
            pollTimer.current = null;
            loadRuns();
          }
        } catch (err) {
          pollTimer.current = null;
          showApiErrorToast(toast, err, { title: "Couldn't follow the codification" });
        }
      };
      pollTimer.current = setTimeout(tick, POLL_MS);
    },
    [loadRuns, stopPolling, toast]
  );

  // The specifications to choose from, when the Start modal opens
  useEffect(() => {
    if (!isStartOpen) return;
    setSpecificationId(defaultSpecificationId || "");
    if (specifications) return;
    request.current
      .get(`${CODIFY_BASE}/specifications?page=1&pageSize=200`)
      .then((res) => {
        const list = asList(unwrapApiResponse(res.data));
        setSpecifications(list);
        if (!defaultSpecificationId && list[0]) setSpecificationId(list[0]._id);
      })
      .catch((err) => {
        setSpecifications([]);
        showApiErrorToast(toast, err, { title: "Couldn't load the specifications" });
      });
  }, [isStartOpen, defaultSpecificationId, specifications, toast]);

  const start = async () => {
    if (!specificationId) return;
    setStarting(true);
    try {
      const res = await request.current.post(`${DOCUMENTS_BASE}/documents/${encodeURIComponent(documentId)}/codify`, {
        specificationId,
      });
      const queued = unwrapApiResponse(res.data);
      setRun(queued);
      onStartClose();
      setPanelOpen(true);
      loadRuns();
      if (queued?._id && isActive(queued)) follow(queued._id);
    } catch (err) {
      showApiErrorToast(toast, err, { title: "Couldn't start the codification" });
    } finally {
      setStarting(false);
    }
  };

  const openRuns = () => {
    onStartClose();
    setRun(null);
    setPanelOpen(true);
    loadRuns();
  };

  const openRun = (r) => {
    setRun(r);
    if (isActive(r)) follow(r._id);
    else stopPolling();
  };

  const closePanel = () => {
    stopPolling();
    setPanelOpen(false);
  };

  const copy = async (text) => {
    try {
      const parsed = parse(text);
      await navigator.clipboard.writeText(parsed && typeof parsed === "object" ? JSON.stringify(parsed, null, 2) : String(text));
      toast({ title: "JSON copied", status: "success", duration: 1500 });
    } catch {
      toast({ title: "Couldn't copy to the clipboard", status: "error" });
    }
  };

  return (
    <>
      <Modal isOpen={isStartOpen} onClose={onStartClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Codify document</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="gray.600" mb={3}>
              Codifies every row of the document with a specification, on the server, and assembles the contract JSON.
            </Text>
            <FormControl>
              <FormLabel fontSize="sm">Specification</FormLabel>
              {specifications === null ? (
                <Spinner size="sm" />
              ) : (
                <Select value={specificationId} onChange={(e) => setSpecificationId(e.target.value)}>
                  <option value="" disabled>Choose a specification</option>
                  {specifications.map((s) => (
                    <option key={s._id} value={s._id}>{s.name || s._id}{s.contractSchemaId ? ` · ${s.contractSchemaId}` : ""}</option>
                  ))}
                </Select>
              )}
            </FormControl>
          </ModalBody>
          <ModalFooter justifyContent="space-between">
            <Button variant="link" size="sm" onClick={openRuns}>Past runs</Button>
            <HStack>
              <Button variant="ghost" onClick={onStartClose}>Cancel</Button>
              <Button colorScheme="blue" onClick={start} isLoading={starting} isDisabled={!specificationId}>Start</Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Drawer isOpen={panelOpen} onClose={closePanel} placement="right" size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            {run ? (
              <Button variant="link" size="sm" onClick={() => { stopPolling(); setRun(null); }}>← Codification runs</Button>
            ) : (
              "Codification runs"
            )}
          </DrawerHeader>
          <DrawerBody py={4} bg="gray.50">
            {run ? (
              <RunDetail run={run} onRowClick={onRowClick} onCopy={copy} />
            ) : runs === null ? (
              <Spinner />
            ) : runs.length === 0 ? (
              <Text fontSize="sm" color="gray.500">No runs for this document yet.</Text>
            ) : (
              <VStack align="stretch" spacing={2}>
                {runs.map((r) => (
                  <Box key={r._id} as="button" type="button" textAlign="left" borderWidth="1px" borderRadius="md" bg="white" p={3}
                    _hover={{ borderColor: "blue.300" }} onClick={() => openRun(r)}>
                    <HStack justify="space-between">
                      <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>{r.specificationName || "Codification"}</Text>
                      <Badge colorScheme={STATE_COLOURS[r.state] || "gray"} textTransform="none">{r.state}</Badge>
                    </HStack>
                    <Text fontSize="xs" color="gray.500">
                      {r.trigger || "—"} · {when(r.completedDate) || (isActive(r) ? "in progress" : when(r.startedDate || r.createdDate))}
                      {r.rowsTotal ? ` · ${r.rowsDone || 0}/${r.rowsTotal} rows` : ""}
                    </Text>
                  </Box>
                ))}
              </VStack>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default DocumentCodifyRuns;
